/**
 * lib/classSort.ts
 * ------------------------------------------------------------------
 * The one canonical order for class lists / dropdowns across the app:
 * ascending by form — Form 1 → 2 → 3 → 4 → 5 → AS → A2 — and, within a
 * form, the streams alphabetically (so Form 1 A, B, K, …; AS Jan A/B/C
 * before AS June; A2 June last). Use sortClasses() wherever a class list
 * or picker is built so the order is identical everywhere.
 * ------------------------------------------------------------------ */
import type { SchoolClass } from '@/types';

const FORM_RANK: Record<string, number> = {
  '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, AS: 6, A2: 7,
};

function formRank(form?: string): number {
  return FORM_RANK[String(form ?? '').toUpperCase().trim()] ?? 90;
}

/** Comparator: form ascending, then stream name alphabetically (case- and
 *  number-aware). Non-standard forms (e.g. Day Care) sort after A2. */
export function compareClasses(a: Pick<SchoolClass, 'form' | 'name'>, b: Pick<SchoolClass, 'form' | 'name'>): number {
  const fr = formRank(a.form) - formRank(b.form);
  if (fr !== 0) return fr;
  return (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' });
}

/** Non-mutating sorted copy in the canonical order. */
export function sortClasses<T extends Pick<SchoolClass, 'form' | 'name'>>(classes: T[]): T[] {
  return [...classes].sort(compareClasses);
}
